/**
 * Shared Utilities Module
 * Common utility functions used across multiple JavaScript modules
 */

/**
 * Debounce function to limit how often a function can be called
 * @param {Function} func - The function to debounce
 * @param {number} wait - The delay in milliseconds
 * @returns {Function} - The debounced function
 */
export function debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
        const later = () => {
            clearTimeout(timeout);
            func(...args);
        };
        clearTimeout(timeout);
        timeout = setTimeout(later, wait);
    };
}

/**
 * Check if DOM is ready
 * @param {Function} callback - Function to call when DOM is ready
 */
export function onDOMReady(callback) {
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', callback);
    } else {
        callback();
    }
}

/**
 * Check if element exists and is visible
 * @param {string|Element} selector - CSS selector or element
 * @returns {boolean} - Whether element exists and is visible
 */
export function elementExists(selector) {
    const element = typeof selector === 'string' ? document.querySelector(selector) : selector;
    return element && element.offsetParent !== null;
}

/**
 * Safe event listener binding with existence check
 * @param {string|Element} element - Element or selector
 * @param {string} event - Event type
 * @param {Function} handler - Event handler
 * @param {Object} options - Event listener options
 */
export function safeAddEventListener(element, event, handler, options = {}) {
    const el = typeof element === 'string' ? document.querySelector(element) : element;
    if (el) {
        el.addEventListener(event, handler, options);
    }
}

/**
 * Get viewport dimensions
 * @returns {Object} - Object with width and height
 */
export function getViewport() {
    return {
        width: window.innerWidth,
        height: window.innerHeight
    };
}

/**
 * Check if current page is admin/editor
 * @returns {boolean} - Whether in admin context
 */
export function isAdmin() {
    return document.body.classList.contains('wp-admin') || 
           document.body.classList.contains('block-editor-page');
}

/**
 * Log message only in development
 * @param {string} message - Message to log
 * @param {any} data - Optional data to log
 */
export function devLog(message, data = null) {
    // Only log in development mode
    if (typeof console !== 'undefined' && console.log && window.location.hostname === 'localhost') {
        if (data) {
            console.log(message, data);
        } else {
            console.log(message);
        }
    }
} 